import React, { useState } from "react";
import {
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Snackbar,
  Grid,
  useTheme,
  makeStyles,
  Fade,
  LinearProgress,
  CircularProgress,
  Backdrop
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { CheckCircle, Settings, Security, Assessment, CloudDone } from "@material-ui/icons";
import base from "../../config";
import { headers } from "../../helper/api";

// Consolidated Step Components
import ClientIdPSetup from "./steps/ClientIdPSetup";
import IntegrationConfig from "./steps/IntegrationConfig";
import AccessClaimsConfig from "./steps/AccessClaimsConfig";
import TestingValidation from "./steps/TestingValidation";
import SSOHeader from "./SSOHeader";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#f5f5f5',
    minHeight: '100vh',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  introSection: {
    background: 'linear-gradient(135deg, #A100FF 0%, #7500C0 50%, #6A1B9A 100%)',
    borderBottom: '1px solid #e1e5e9',
    padding: theme.spacing(6, 3),
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.08'%3E%3Cpath d='M0 40L40 0h40v40L40 80H0V40z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    },
  },
  introContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    textAlign: 'center',
    position: 'relative',
    zIndex: 1,
  },
  mainTitle: {
    fontSize: '2.5rem',
    fontWeight: 700,
    color: '#ffffff',
    marginBottom: theme.spacing(1),
    letterSpacing: '-0.025em',
    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  subtitle: {
    fontSize: '1.25rem',
    color: 'rgba(255,255,255,0.9)',
    marginBottom: theme.spacing(4),
    fontWeight: 400,
    lineHeight: 1.6,
  },
  progressWrapper: {
    maxWidth: '600px',
    margin: '0 auto',
  },
  progressLabel: {
    fontSize: '0.875rem',
    color: 'rgba(255,255,255,0.9)',
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    display: 'block',
  },
  progressBar: {
    height: '8px',
    borderRadius: '12px',
    backgroundColor: 'rgba(255,255,255,0.3)',
    '& .MuiLinearProgress-bar': {
      borderRadius: '12px',
      background: 'linear-gradient(90deg, #ffffff 0%, rgba(255,255,255,0.8) 100%)',
    },
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    backgroundColor: '#ffffff',
  },
  stepperSidebar: {
    width: '320px',
    backgroundColor: '#f8f9fa',
    padding: theme.spacing(3, 2),
    display: 'flex',
    flexDirection: 'column',
  },
  stepperCard: {
    backgroundColor: '#ffffff',
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
  },
  stepperCardHeader: {
    background: 'linear-gradient(135deg, #6A1B9A 0%, #8E24AA 100%)',
    padding: theme.spacing(2.5, 3),
    color: '#ffffff',
  },
  stepperTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#ffffff',
    textAlign: 'center',
    margin: 0,
  },
  stepperCardContent: {
    padding: theme.spacing(2),
  },
  stepper: {
    background: 'transparent',
    padding: 0,
  },
  stepLabel: {
    '& .MuiStepLabel-label': {
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    '& .MuiStepLabel-label.MuiStepLabel-active': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
    '& .MuiStepLabel-label.MuiStepLabel-completed': {
      color: theme.palette.text.primary,
      fontWeight: 500,
    },
  },
  contentArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  contentCard: {
    flex: 1,
    margin: theme.spacing(3),
    borderRadius: theme.spacing(2),
    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    background: 'white',
  },
  cardHeader: {
    background: `linear-gradient(90deg, ${theme.palette.grey[50]} 0%, ${theme.palette.background.paper} 100%)`,
    padding: theme.spacing(3, 4),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.primary.main,
    fontSize: '1.5rem',
    fontWeight: 600,
  },
  stepContent: {
    flex: 1,
    padding: theme.spacing(4),
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(3, 4),
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.grey[50],
  },
  navigationButton: {
    minWidth: '120px',
    height: '40px',
    borderRadius: '20px',
    fontSize: '0.875rem',
    fontWeight: 600,
    textTransform: 'none',
  },
  backButton: {
    color: theme.palette.text.secondary,
    borderColor: theme.palette.divider,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
  },
  nextButton: {
    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
    boxShadow: `0 3px 5px 2px ${theme.palette.primary.main}40`,
    '&:hover': {
      background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.secondary.dark} 90%)`,
    },
  },
  progressIndicator: {
    marginTop: theme.spacing(2),
  },
  stepIcon: {
    marginRight: theme.spacing(1),
  },
  loadingBackdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#ffffff',
    backdropFilter: 'blur(4px)',
    backgroundColor: 'rgba(106, 27, 154, 0.7)',
  },
  loadingContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(3),
  },
  loadingText: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#ffffff',
    textAlign: 'center',
  },
  loadingSubtext: {
    fontSize: '1rem',
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    marginTop: theme.spacing(1),
  },
}));

const steps = [
  {
    label: "Client & IdP Setup",
    icon: Settings,
    description: "Basic client and identity provider configuration"
  },
  {
    label: "Integration Config",
    icon: Security,
    description: "Setup integration details and applications"
  },
  {
    label: "Access & Claims",
    icon: Assessment,
    description: "Configure role mappings and claim mappings"
  },
  {
    label: "Review & Submit",
    icon: CloudDone,
    description: "Add test users, review configuration and submit request"
  }
];

const ExternalSSOStepper = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  // Form data state
  const [formData, setFormData] = useState({
    clientSetup: {
      clientName: "",
      clientCode: "",
      environment: "",
      technicalContact: "",
      hostingType: ""
    },
    idpConfig: {
      provider: "",
      protocol: "",
      tenantId: "",
      metadataUrl: "",
      entityId: "",
      certificateExpiry: "",
      endpoints: []
    },
    integrationDetails: {
      spEntityId: "",
      acsUrl: "",
      singleLogoutUrl: ""
    },
    applications: [],
    roleMappings: [],
    claimMappings: {
      email: "",
      givenName: "",
      familyName: "",
      groups: ""
    },
    testUsers: [],
    testResults: null,
    integrationStatus: {
      intakeCompleted: false,
      idpConnected: false,
      claimsMapped: false,
      provisioning: false,
      completed: false
    }
  });

  // Validation functions
  const validateStep1 = () => {
    const clientData = formData.clientSetup;
    const idpData = formData.idpConfig;

    return (
      clientData.clientName && clientData.clientName.trim().length >= 2 &&
      clientData.clientCode && /^[a-zA-Z0-9_-]{2,20}$/.test(clientData.clientCode.trim()) &&
      clientData.environment &&
      clientData.technicalContact && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientData.technicalContact.trim()) &&
      clientData.hostingType &&
      idpData.provider &&
      idpData.protocol
      // tenantId is now optional - removed from validation
    );
  };

  const validateStep2 = () => {
    return (
      formData.applications && formData.applications.length > 0 &&
      formData.integrationDetails && formData.integrationDetails.spEntityId
    );
  };

  const validateStep3 = () => {
    const hasValidRoleMappings = formData.roleMappings && formData.roleMappings.length > 0 &&
      formData.roleMappings.every(mapping => {
        const hasValidGroups = Array.isArray(mapping.adGroups)
          ? mapping.adGroups.length > 0 && mapping.adGroups.some(group => group && group.trim())
          : (mapping.adGroup && mapping.adGroup.trim()); // Backward compatibility

        return mapping.application && hasValidGroups && mapping.role;
      });

    const hasValidClaimMappings = formData.claimMappings &&
      Object.keys(formData.claimMappings).filter(key => formData.claimMappings[key]).length > 0;

    return hasValidRoleMappings && hasValidClaimMappings;
  };

  const canProceedToNextStep = () => {
    switch (activeStep) {
      case 0:
        return validateStep1();
      case 1:
        return validateStep2();
      case 2:
        return validateStep3();
      default:
        return true;
    }
  };

  const canSubmit = () => {
    return validateStep1() && validateStep2() && validateStep3();
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => {
      // Mark current step as completed when moving forward
      setCompletedSteps(prev => new Set([...prev, prevActiveStep]));
      return prevActiveStep + 1;
    });
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    if (canSubmit()) {
      setLoading(true);

      try {
        // Prepare the payload
        const payload = {
          timestamp: new Date().toISOString(),
          requestId: `SSO_${Date.now()}_${Math.random().toString(36).substring(7)}`,
          clientConfiguration: {
            clientName: formData.clientSetup.clientName,
            clientCode: formData.clientSetup.clientCode,
            environment: formData.clientSetup.environment,
            technicalContact: formData.clientSetup.technicalContact,
            hostingType: formData.clientSetup.hostingType
          },
          identityProvider: (() => {
            const idp = {
              provider: formData.idpConfig.provider,
              protocol: formData.idpConfig.protocol
            };

            // Add tenantId only if provided (it's optional)
            if (formData.idpConfig.tenantId) {
              idp.tenantId = formData.idpConfig.tenantId;
            }

            // Add protocol-specific fields
            if (formData.idpConfig.protocol === 'SAML') {
              if (formData.idpConfig.metadataUrl) {
                idp.metadataUrl = formData.idpConfig.metadataUrl;
              }
            } else if (formData.idpConfig.protocol === 'OIDC') {
              if (formData.idpConfig.issuerUrl) {
                idp.issuerUrl = formData.idpConfig.issuerUrl;
              }
              if (formData.idpConfig.clientId) {
                idp.clientId = formData.idpConfig.clientId;
              }
            }

            return idp;
          })(),
          integrationDetails: {
            spEntityId: formData.integrationDetails.spEntityId,
            acsUrl: formData.integrationDetails.acsUrl,
            singleLogoutUrl: formData.integrationDetails.singleLogoutUrl
          },
          applications: formData.applications.map(app => ({
            name: app.name,
            url: app.url || "",
            description: app.description || "",
            hostingDetails: app.hostingDetails || {},
            privateDns: app.privateDns || null,
            cluster: app.cluster || null,
            clusterRegion: app.clusterRegion || null,
            clusterName: app.clusterName || null
          })),
          accessControl: {
            roleMappings: formData.roleMappings.map(mapping => {
              const baseMapping = {
                application: mapping.application,
                role: mapping.role,
                adGroupType: mapping.adGroupType || 'CLIENT'
              };

              // Split AD groups based on type for better workflow tracking
              if (mapping.adGroupType === 'ACCENTURE') {
                baseMapping.accentureAdGroups = mapping.adGroups || [];
                baseMapping.adGroups = []; // Keep empty for backward compatibility
              } else {
                baseMapping.adGroups = mapping.adGroups || [];
                baseMapping.clientAdGroups = mapping.adGroups || []; // Explicit client groups
              }

              return baseMapping;
            }),
            claimMappings: {
              email: formData.claimMappings.email || "",
              givenName: formData.claimMappings.givenName || "",
              familyName: formData.claimMappings.familyName || "",
              groups: formData.claimMappings.groups || ""
            }
          },
          testUsers: formData.testUsers.map(user => ({
            email: user.email,
            adGroups: user.adGroups || [],
            // Backward compatibility for legacy groups field
            groups: user.groups || null
          })),
          metadata: {
            submittedBy: formData.clientSetup.technicalContact,
            submissionTime: new Date().toISOString(),
            source: "CAM - Client SSO Integration Wizard"
          }
        };

        // Get idToken from localStorage (following app's auth pattern)
        const idToken = localStorage.getItem("token");

        // Make the POST request using base URL configuration and app's auth pattern
        const ssoApiUrl = `${base.app.apiSite}sso/submit`;
        const response = await fetch(ssoApiUrl, {
          method: 'POST',
          headers: {
            ...headers,
            Authorization: idToken
          },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          const result = await response.json();
          setSubmitted(true);
          setSubmissionResult(result);
          setSnackbar({
            open: true,
            message: `SSO integration request submitted successfully! Request ID: ${result.requestId || payload.requestId}`,
            severity: "success"
          });
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (error) {
        console.error('Error submitting SSO integration request:', error);
        setSnackbar({
          open: true,
          message: `Failed to submit request: ${error.message}`,
          severity: "error"
        });
      } finally {
        setLoading(false);
      }
    } else {
      // Show detailed validation errors
      const missingItems = [];
      if (!validateStep1()) missingItems.push("Client & Identity Provider setup");
      if (!validateStep2()) missingItems.push("Integration configuration and applications");
      if (!validateStep3()) missingItems.push("Access control and claim mappings");

      setSnackbar({
        open: true,
        message: `Please complete the following sections: ${missingItems.join(", ")}`,
        severity: "error"
      });
    }
  };

  const updateFormData = (section, data) => {
    setFormData(prevState => ({
      ...prevState,
      [section]: data
    }));
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <ClientIdPSetup
            clientData={formData.clientSetup}
            idpData={formData.idpConfig}
            onClientUpdate={(data) => updateFormData('clientSetup', data)}
            onIdPUpdate={(data) => updateFormData('idpConfig', data)}
          />
        );
      case 1:
        return (
          <IntegrationConfig
            integrationData={formData.integrationDetails}
            applicationsData={formData.applications}
            protocol={formData.idpConfig.protocol}
            hostingType={formData.clientSetup.hostingType}
            clientData={formData.clientSetup}
            onIntegrationUpdate={(data) => updateFormData('integrationDetails', data)}
            onApplicationsUpdate={(data) => updateFormData('applications', data)}
          />
        );
      case 2:
        return (
          <AccessClaimsConfig
            roleMappings={formData.roleMappings}
            claimMappings={formData.claimMappings}
            applications={formData.applications}
            protocol={formData.idpConfig.protocol}
            onRoleMappingUpdate={(data) => updateFormData('roleMappings', data)}
            onClaimMappingUpdate={(data) => updateFormData('claimMappings', data)}
          />
        );
      case 3:
        return (
          <TestingValidation
            testUsers={formData.testUsers}
            formData={formData}
            onTestUsersUpdate={(data) => updateFormData('testUsers', data)}
            onEdit={(step) => setActiveStep(step)}
            onSubmit={handleSubmit}
            submitted={submitted}
            canSubmit={canSubmit()}
            submissionResult={submissionResult}
          />
        );
      default:
        return <Typography>Unknown step</Typography>;
    }
  };

  const progressPercentage = submitted ? 100 : (activeStep / steps.length) * 100;
  const StepIcon = steps[activeStep].icon;

  return (
    <div className={classes.root}>
      {/* Custom SSO Header */}
      <SSOHeader />

      <div className={classes.container}>
        {/* Introduction Section */}
        <div className={classes.introSection}>
          <div className={classes.introContent}>
            <Typography className={classes.mainTitle}>
              SSO Integration Setup
            </Typography>
            <Typography className={classes.subtitle}>
              Configure your Identity Provider integration with our guided step-by-step process
            </Typography>

            <div className={classes.progressWrapper}>
              <Typography className={classes.progressLabel}>
                {submitted ? 'Integration Request Completed Successfully!' :
                 `Step ${activeStep + 1} of ${steps.length} - ${Math.round(progressPercentage)}% Complete`}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progressPercentage}
                className={classes.progressBar}
              />
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className={classes.mainContent}>
          {/* Stepper Sidebar */}
          <div className={classes.stepperSidebar}>
            <div className={classes.stepperCard}>
              <div className={classes.stepperCardHeader}>
                <Typography className={classes.stepperTitle}>
                  Configuration Steps
                </Typography>
              </div>
              <div className={classes.stepperCardContent}>
                <Stepper
                  activeStep={activeStep}
                  orientation="vertical"
                  className={classes.stepper}
                >
              {steps.map((step, index) => {
                const IconComponent = step.icon;

                // Check if step is completed based on navigation, not validation
                const isStepCompleted = completedSteps.has(index) || (submitted && index <= 3);

                return (
                  <Step key={step.label} completed={isStepCompleted}>
                    <StepLabel
                      className={classes.stepLabel}
                      icon={
                        isStepCompleted ? (
                          <CheckCircle className={classes.stepIcon} style={{ color: theme.palette.success.main }} />
                        ) : (
                          <IconComponent className={classes.stepIcon} />
                        )
                      }
                    >
                      <Box>
                        <Typography variant="body1" style={{ fontWeight: 500 }}>
                          {step.label}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {step.description}
                        </Typography>
                      </Box>
                    </StepLabel>
                  </Step>
                );
              })}
                </Stepper>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className={classes.contentArea}>
            <Fade in={true} timeout={400}>
              <Card className={classes.contentCard}>
                <div className={classes.cardHeader}>
                  <Typography className={classes.cardTitle}>
                    <StepIcon />
                    {steps[activeStep].label}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {steps[activeStep].description}
                  </Typography>
                </div>

                <div className={classes.stepContent}>
                  {renderStepContent(activeStep)}
                </div>

                {/* Navigation Buttons */}
                <div className={classes.buttonContainer}>
                  <Button
                    disabled={activeStep === 0 || submitted}
                    onClick={handleBack}
                    variant="outlined"
                    className={`${classes.navigationButton} ${classes.backButton}`}
                  >
                    Previous
                  </Button>
                  {activeStep < steps.length - 1 && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleNext}
                      disabled={loading}
                      className={`${classes.navigationButton} ${classes.nextButton}`}
                    >
                      Continue
                    </Button>
                  )}
                </div>
              </Card>
            </Fade>
          </div>
        </div>

        {/* Loading Backdrop */}
        <Backdrop className={classes.loadingBackdrop} open={loading}>
          <div className={classes.loadingContent}>
            <CircularProgress size={60} thickness={4} style={{ color: '#ffffff' }} />
            <div>
              <Typography className={classes.loadingText}>
                Submitting Integration Request
              </Typography>
              <Typography className={classes.loadingSubtext}>
                Please wait while we process your SSO configuration...
              </Typography>
            </div>
          </div>
        </Backdrop>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default ExternalSSOStepper;